/**
 * Shared editor store base state and actions for loading, progress, and error tracking.
 *
 * Both the S3K and Roland editors share these fields and behaviors. This module
 * provides the interface and a Zustand slice creator so each editor composes
 * the base into its own store without duplicating logic.
 *
 * Usage with Zustand's create():
 *
 *   const useEditorStore = create<MyState & EditorStoreBase>((set) => ({
 *     ...createEditorStoreSlice(set),
 *     // device-specific state and actions
 *   }));
 */
/**
 * Initial state values for the editor store base.
 */
export const editorStoreBaseInitialState = {
    isLoading: false,
    loadingMessage: null,
    loadingProgress: null,
    error: null,
};
/**
 * Creates the base slice of state and actions for an editor store.
 *
 * The returned object can be spread into a Zustand `create()` call alongside
 * device-specific state. Actions use the provided `set` function so they
 * participate in the same store instance.
 *
 * @param set - The Zustand `set` function from the enclosing `create()` call.
 */
export function createEditorStoreSlice(set) {
    return {
        ...editorStoreBaseInitialState,
        setLoading(isLoading, message) {
            set({
                isLoading,
                loadingMessage: message ?? null,
                ...(isLoading ? {} : { loadingProgress: null }),
            });
        },
        setProgress(current, total) {
            const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
            set({ loadingProgress: percentage });
        },
        clearProgress() {
            set({ loadingProgress: null });
        },
        setError(error) {
            // Clearing an error (error === null) must NOT touch loading state.
            // Previous contract reset isLoading: false on every setError call, which
            // made `setLoading(true); setError(null);` a self-cancelling pair — the
            // PatchesPage / TonesPage percent-bar progress region was unreachable
            // because useBankLoader fires that exact sequence at the start of every
            // bank load. Fixed 2026-05-11 per decisions-2026-05-11 Decision 5.
            if (error === null) {
                set({ error: null });
                return;
            }
            // Setting a non-null error implies failure — clear in-flight loading state.
            set({
                error,
                isLoading: false,
                loadingMessage: null,
                loadingProgress: null,
            });
        },
    };
}
//# sourceMappingURL=editorStoreBase.js.map